import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

import { ReportTypeCategory, ReportSourceType, ApiAccess } from '@/core/interfaces/common';
import { FilterContainer } from '@/core/components/FilterContainer';
import { Paragraph } from '@/core/components/Typography';
import {
  applyFilter,
  getAppliedFilterSelector,
  isFilterOpenedSelector,
} from '@/core/store/reducers/filtersSlice';
import { AppliedFilterData, AppliedFilterDataItem, FilterType } from '@/core/interfaces/filters';
import { useAppDispatch } from '@/core/store/store';
import { MultiButton, MultiButtonItem } from '@/core/components/MultiButton';
import {
  impactFilterOptions,
  impactFilterOptionsG4S,
  sourceCredibilityFilterOptions,
} from '@/core/constants/filters';

import { SourceCredibility } from '@/features/Reports/interfaces';
import { getUserSelector } from '@/features/Auth/store';

import { isG4S } from '@/utils/helpers';

import { FilterGroup } from './MoreFilters.styles';

const sourceCredibilityFilterValues = sourceCredibilityFilterOptions.map(option => option.value);

const getSourceCredibilityReliabilityColor = (
  value: SourceCredibility
): 'high' | 'medium' | 'low' | 'unknown' => {
  switch (value) {
    case SourceCredibility.RELIABLE:
      return 'high';
    case SourceCredibility.PARTIALLY_RELIABLE:
      return 'medium';
    case SourceCredibility.UNRELIABLE:
      return 'low';
    default:
      return 'unknown';
  }
};

const removeSourceCredibilityFilters = (filters: AppliedFilterData): AppliedFilterData =>
  filters.filter(
    filter => !sourceCredibilityFilterValues.includes(filter.value as SourceCredibility)
  );

export const MoreFilters = () => {
  const { colors } = useTheme();
  const user = useSelector(getUserSelector);
  const appliedFilters = useSelector(state =>
    getAppliedFilterSelector(state, FilterType.MORE_FILTERS)
  );
  const dispatch = useAppDispatch();

  const [selectedFilters, setSelectedFilters] = useState<AppliedFilterData>(appliedFilters);
  const isFilterVisible = useSelector(state =>
    isFilterOpenedSelector(state, FilterType.MORE_FILTERS)
  );

  const reportSourceOptions = useMemo(() => {
    const sourceOptions: Array<MultiButtonItem<string>> = [];

    if (user?.organization.currentSubscription.apiAccess.includes(ApiAccess.HORIZON)) {
      sourceOptions.push({
        label: 'Verified',
        value: ReportSourceType.HORIZON,
        iconName: 'Person' as const,
      });
    }

    if (user?.organization.currentSubscription.apiAccess.includes(ApiAccess.LOOKOUT)) {
      sourceOptions.push({
        label: 'Hozint AI',
        value: ReportSourceType.LOOKOUT,
        iconName: 'MicrochipAi' as const,
      });
    }

    return sourceOptions;
  }, [user?.organization.currentSubscription.apiAccess]);

  const setAppliedFilters = useCallback(
    (filtersToApply: AppliedFilterData) => {
      dispatch(
        applyFilter({
          filterType: FilterType.MORE_FILTERS,
          filterData: filtersToApply,
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    if (!isFilterVisible) {
      if (!appliedFilters.length) {
        setSelectedFilters([]);
      } else {
        setSelectedFilters(appliedFilters);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterVisible]);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters(selectedFilters);
  }, [selectedFilters, setAppliedFilters]);

  useEffect(() => {
    setSelectedFilters(appliedFilters);
  }, [appliedFilters]);

  const reportImpactOptions = useMemo(() => {
    let impactOptions = impactFilterOptions;

    if (isG4S) {
      impactOptions = impactFilterOptionsG4S;
    }

    return impactOptions;
  }, []);

  const reportTypeCategoryOptions = useMemo(() => {
    return Object.values(ReportTypeCategory).map(value => ({
      label: value,
      value,
    }));
  }, []);

  const isLookoutSelected = useMemo(
    () => selectedFilters.some(filter => filter.value === ReportSourceType.LOOKOUT),
    [selectedFilters]
  );

  const sourceCredibilityOptions = useMemo(
    () =>
      sourceCredibilityFilterOptions.map(option => ({
        ...option,
        activeBackgroundColor:
          colors.reliability[getSourceCredibilityReliabilityColor(option.value)],
        activeTextColor: '#061733',
      })),
    [colors.reliability]
  );

  const handleSelectFilter = useCallback(
    (filters: Array<string>) => {
      const isLookoutDataFeedSelected = filters.includes(ReportSourceType.LOOKOUT);
      const sanitizedFilters = isLookoutDataFeedSelected
        ? filters
        : filters.filter(
            filter => !sourceCredibilityFilterValues.includes(filter as SourceCredibility)
          );

      setSelectedFilters(prevFilters => {
        const filtersToRemove = prevFilters.filter(
          filter => !sanitizedFilters.includes(filter.value as string)
        );

        const filtersToAdd = sanitizedFilters
          .filter(filter => !prevFilters.some(prevFilter => prevFilter.value === filter))
          .map(filter => {
            if (filter === ReportSourceType.HORIZON || filter === ReportSourceType.LOOKOUT) {
              return {
                value: filter,
                label:
                  (reportSourceOptions.find(option => option.value === filter)?.label as string) ||
                  '',
              };
            }

            if (reportImpactOptions.some(option => option.value === filter)) {
              return reportImpactOptions.find(
                option => option.value === filter
              ) as AppliedFilterDataItem;
            }

            if (sourceCredibilityOptions.some(option => option.value === filter)) {
              return sourceCredibilityOptions.find(
                option => option.value === filter
              ) as AppliedFilterDataItem;
            }

            return {
              value: filter,
              label: filter,
            };
          });

        return [
          ...prevFilters.filter(filter => !filtersToRemove.includes(filter)),
          ...filtersToAdd,
        ];
      });
    },
    [reportImpactOptions, reportSourceOptions, sourceCredibilityOptions]
  );

  const getAppliedFilterLabel = useCallback(
    (item: AppliedFilterDataItem) => {
      const impactOption = reportImpactOptions.find(option => option.value === item.value);

      if (impactOption) {
        return impactOption.label as string;
      }

      const sourceOption = reportSourceOptions.find(option => option.value === item.value);

      if (sourceOption) {
        return sourceOption.label as string;
      }

      const credibilityOption = sourceCredibilityOptions.find(
        option => option.value === item.value
      );

      if (credibilityOption) {
        return credibilityOption.label as string;
      }

      const reportTypeOption = reportTypeCategoryOptions.find(
        option => option.value === item.value
      );

      if (reportTypeOption) {
        return reportTypeOption.label as string;
      }

      return item.label;
    },
    [reportImpactOptions, reportSourceOptions, sourceCredibilityOptions, reportTypeCategoryOptions]
  );

  const getAppliedFilterGroupLabel = useCallback(
    (item: AppliedFilterDataItem) => {
      if (reportImpactOptions.some(option => option.value === item.value)) {
        return 'Impact:';
      }

      if (reportSourceOptions.some(option => option.value === item.value)) {
        return 'Data feed:';
      }

      if (sourceCredibilityOptions.some(option => option.value === item.value)) {
        return 'Source credibility:';
      }

      if (reportTypeCategoryOptions.some(option => option.value === item.value)) {
        return 'Report type:';
      }

      return undefined;
    },
    [reportImpactOptions, reportSourceOptions, sourceCredibilityOptions, reportTypeCategoryOptions]
  );

  const handleRemoveFilterFromList = useCallback(
    (filter: AppliedFilterDataItem) => {
      let filterdSelectedFiltersList = selectedFilters.filter(
        selectedFilter => selectedFilter.value !== filter.value
      );
      let filterdAppliedFiltersList = appliedFilters.filter(
        appliedFilter => appliedFilter.value !== filter.value
      );

      if (filter.value === ReportSourceType.LOOKOUT) {
        filterdSelectedFiltersList = removeSourceCredibilityFilters(filterdSelectedFiltersList);
        filterdAppliedFiltersList = removeSourceCredibilityFilters(filterdAppliedFiltersList);
      }

      setSelectedFilters(filterdSelectedFiltersList);
      setAppliedFilters(filterdAppliedFiltersList);
    },
    [appliedFilters, selectedFilters, setAppliedFilters]
  );

  return (
    <FilterContainer
      filterType={FilterType.MORE_FILTERS}
      onFiltersApply={handleApplyFilters}
      filterName="More Filters"
      onAppliedFilterRemove={handleRemoveFilterFromList}
      getAppliedFilterLabel={getAppliedFilterLabel}
      getAppliedFilterGroupLabel={getAppliedFilterGroupLabel}
    >
      <FilterGroup>
        <Paragraph type="normal">Impact</Paragraph>
        <MultiButton
          isMulti
          isSmall
          active={selectedFilters.map(filter => filter.value as string)}
          tabsData={reportImpactOptions}
          onChange={handleSelectFilter}
        />
      </FilterGroup>
      <FilterGroup>
        <Paragraph type="normal">Data feed</Paragraph>
        <MultiButton
          isMulti
          isSmall
          active={selectedFilters.map(filter => filter.value as string)}
          tabsData={reportSourceOptions}
          onChange={handleSelectFilter}
        />
      </FilterGroup>
      {isLookoutSelected && (
        <FilterGroup>
          <Paragraph type="normal">Source credibility</Paragraph>
          <MultiButton
            isMulti
            isSmall
            active={selectedFilters.map(filter => filter.value as string)}
            tabsData={sourceCredibilityOptions}
            onChange={handleSelectFilter}
          />
        </FilterGroup>
      )}
      <FilterGroup>
        <Paragraph type="normal">Report type</Paragraph>
        <MultiButton
          isMulti
          isSmall
          active={selectedFilters.map(filter => filter.value as string)}
          tabsData={reportTypeCategoryOptions}
          onChange={handleSelectFilter}
        />
      </FilterGroup>
    </FilterContainer>
  );
};
